import { ClickAwayListener, Fade, Popper } from '@mui/material';
import { ListBoxMenu } from './ListBoxMenu';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { popperStyles, resolveFontSize } from './listBoxDefaultStyle';
import { ListBoxMessage } from './ListBoxMessage';
import { Box } from '../../Box';
import { Spinner } from '../../Spinner';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { PlusIcon } from '../../Icons';
import { idBuilder } from '../../internal';

/**
 * Used to set the width of the popper to the same width as the select input
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const sameWidth = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: _ref => {
    let {
      state
    } = _ref;
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: _ref2 => {
    let {
      state
    } = _ref2;
    state.elements.popper.style.width = `${state.elements.reference.getBoundingClientRect().width}px`;
  }
};
const popperOffset = {
  name: 'offset',
  options: {
    offset: [0, 4]
  }
};
const buildModifiers = (maxWidth, minWidth) => {
  const modifiers = [];
  modifiers.push(popperOffset);
  if (maxWidth === 'anchorWidth' || minWidth === 'anchorWidth') {
    modifiers.push(sameWidth);
  }
  return modifiers;
};
export const ListBox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    placement = 'bottom-start',
    defaultOpenClickOverride,
    defaultSelected,
    selectedItem,
    onClose,
    open,
    anchorEl,
    maxWidth,
    minWidth,
    id,
    options,
    autoFocusItem = true,
    isLoading = false,
    loadingMessage,
    displayNoDataMessage,
    noDataMessage,
    maxHeight,
    size = 'medium',
    allowNewEntryMessage,
    displayAllowNewEntry,
    messageKeyDown,
    messageOnClick,
    ...rest
  } = props;
  const theme = useInterstateTheme();
  const [openState, setOpenState] = useState(open || false);
  const [_listBoxOptions, setListBoxOptions] = useState(options || []);
  const [keyAction, setKeyAction] = useState();
  const [_selectedItem, setSelectedItem] = useState(defaultSelected);
  useImperativeHandle(ref, () => ({
    open: () => setOpenState(true),
    close: () => setOpenState(false),
    updateList: options => setListBoxOptions(options),
    doKeyAction: key => setKeyAction(key),
    setSelectedItem: selectedItem => setSelectedItem(selectedItem)
  }));
  useEffect(() => {
    setSelectedItem(selectedItem);
  });
  useEffect(() => {
    if (options) {
      setListBoxOptions(options);
    } else {
      setListBoxOptions([]);
    }
  }, [options]);
  useEffect(() => {
    setKeyAction(undefined);
  }, [openState]);
  useEffect(() => {
    setKeyAction(undefined);
  }, [_listBoxOptions]);
  useEffect(() => {
    setOpenState(open);
  }, [open]);
  let _menu;
  if (displayAllowNewEntry) {
    _menu = /*#__PURE__*/_jsx(ListBoxMessage, {
      maxWidth: maxWidth || 'auto',
      minWidth: minWidth || 'auto',
      size: size,
      id: id,
      "data-testid": rest['data-testid'],
      style: {
        ':hover': {
          backgroundColor: theme.tokens.SemColorSurfaceInteractiveHover
        }
      },
      onClick: messageOnClick,
      onKeyDown: messageKeyDown,
      children: /*#__PURE__*/_jsxs(Box, {
        sx: {
          fontSize: resolveFontSize(size || 'medium', theme),
          color: theme.tokens.SemColorOnSurfaceDefault,
          display: 'flex',
          gap: '4px',
          justifyContent: 'flex-start',
          alignItems: 'center'
        },
        id: idBuilder('ids-listbox-allownewentry-message', id),
        "data-testid": idBuilder('ids-listbox-allownewentry-message', rest['data-testid']),
        children: [/*#__PURE__*/_jsx(PlusIcon, {}), allowNewEntryMessage]
      })
    });
  } else if (isLoading) {
    _menu = /*#__PURE__*/_jsx(ListBoxMessage, {
      maxWidth: maxWidth || 'auto',
      minWidth: minWidth || 'auto',
      size: size,
      id: id,
      "data-testid": rest['data-testid'],
      style: {
        cursor: 'not-allowed'
      },
      children: /*#__PURE__*/_jsxs(Box, {
        display: 'flex',
        gap: '4px',
        justifyContent: 'center',
        alignItems: 'center',
        color: theme.tokens.SemColorOnSurfaceSubdued,
        sx: {
          cursor: 'not-allowed'
        },
        width: '100%',
        fontSize: resolveFontSize(size, theme),
        id: idBuilder('ids-listbox-isloading-message', id),
        "data-testid": idBuilder('ids-listbox-isloading-message', rest['data-testid']),
        children: [/*#__PURE__*/_jsx(Spinner, {
          size: 20
        }), loadingMessage || 'Loading results']
      })
    });
  } else if (!_listBoxOptions || _listBoxOptions.length === 0 && displayNoDataMessage) {
    _menu = /*#__PURE__*/_jsx(ListBoxMessage, {
      maxWidth: maxWidth || 'auto',
      minWidth: minWidth || 'auto',
      size: size,
      id: id,
      "data-testid": rest['data-testid'],
      style: {
        cursor: 'not-allowed'
      },
      children: /*#__PURE__*/_jsx(Box, {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        sx: {
          cursor: 'not-allowed'
        },
        color: theme.tokens.SemColorOnSurfaceSubdued,
        width: '100%',
        fontSize: resolveFontSize(size, theme),
        id: idBuilder('ids-listbox-nodata-message', id),
        "data-testid": idBuilder('ids-listbox-nodata-message', rest['data-testid']),
        children: noDataMessage || 'No matches found'
      })
    });
  } else {
    _menu = !_listBoxOptions || _listBoxOptions.length === 0 ? null : /*#__PURE__*/_jsx(ListBoxMenu, {
      ...props,
      options: _listBoxOptions,
      doKeyAction: keyAction,
      selectedItem: _selectedItem,
      onSelectItem: setSelectedItem,
      id: id,
      "data-testid": rest['data-testid']
    });
  }
  const listBoxModifiers = buildModifiers(maxWidth, minWidth);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(Popper, {
      className: "interstate-reference",
      open: Boolean(openState),
      anchorEl: anchorEl?.current,
      placement: placement,
      transition: true,
      disablePortal: false,
      modifiers: listBoxModifiers,
      id: id,
      "data-testid": rest['data-testid'],
      sx: popperStyles,
      children: _ref3 => {
        let {
          TransitionProps
        } = _ref3;
        return /*#__PURE__*/_jsx(Fade, {
          ...TransitionProps,
          timeout: 100,
          children: /*#__PURE__*/_jsx("div", {
            children: defaultOpenClickOverride ? _menu : /*#__PURE__*/_jsx(ClickAwayListener, {
              onClickAway: onClose,
              children: /*#__PURE__*/_jsx("div", {
                children: _menu
              })
            })
          })
        });
      }
    })
  });
});
ListBox.displayName = 'ListBox';